import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useGoogleMapsContext, MediaQuery, InfoWindow } from '@grandvisionhq/common';
import { LocationSearchMap } from '@grandvisionhq/locations';
import { useIntl } from '@grandvisionhq/state';
import { Components, useStoreFinderContext } from '@grandvisionhq/stores-v2';
import { aabPreferredStoreClickEvent } from '../../../data-layer/actions.js';
import { fromStoreResultToStore } from '../../../pages/BookAppointment/adapters/from-store-result-to-store.js';
import StoreFinderItemContainer from '../StoreFinderItem/StoreFinderItemContainer.js';

const CurrentLocationPin = Components.CurrentLocationPin;
const currentLocationPin = CurrentLocationPin({
    theme: {
        borderColor: '#2B2E31',
        background: '#FFFFFF'
    }
});
const PreferredStorePin = Components.PreferredStorePin;
const preferredStorePinFallback = PreferredStorePin();
const StoreFinderMapContainer = ({ stores , googleMapsId  })=>{
    const { hoveredStore: hoveredStoreInList , isUsingUserLocation , userLocation , isUserLocationRequested , setIsUserLocationRequested , preferredStore: preferredStore1 , activeMapInfoWindow , setActiveMapInfoWindow ,  } = useStoreFinderContext();
    const { googleMapsSettings: { preferredStorePin  } ,  } = useGoogleMapsContext();
    const { getLabel  } = useIntl();
    const [hoveredStore, setHoveredStore] = React.useState(null);
    const handleOnMarkerHover = (markerId)=>{
        setHoveredStore(markerId);
    };
    const isPhone = MediaQuery.useIsPhone();
    const [activeStore, setActiveStore] = React.useState(null);
    const handleOnClickUseMyLocation = React.useCallback(()=>{
        if (!isUserLocationRequested) {
            setIsUserLocationRequested(true);
        }
    }, [
        isUserLocationRequested,
        setIsUserLocationRequested
    ]);
    const handleOnMarkerClick = React.useCallback((markerId)=>{
        if (activeMapInfoWindow === markerId) {
            setActiveMapInfoWindow(null);
            setActiveStore(null);
            return;
        }
        // We need to expand the list of stores with a preferredStore if present
        const storesWithPreferredStore = preferredStore1 ? [
            ...stores,
            preferredStore1
        ] : stores;
        const store1 = storesWithPreferredStore.find((store)=>store.globalStoreId === markerId
        );
        if (store1) {
            setActiveMapInfoWindow(markerId);
            setActiveStore(fromStoreResultToStore(store1));
        }
    }, [
        activeMapInfoWindow,
        preferredStore1,
        stores,
        setActiveMapInfoWindow
    ]);
    const handleOnMapClick = React.useCallback(()=>{
        setActiveMapInfoWindow(null);
    }, [
        setActiveMapInfoWindow
    ]);
    const handleOnClickPreferredStoreItem = (store)=>{
        DataLayer.pushEvent(aabPreferredStoreClickEvent({
            store,
            itemType: 'map'
        }));
    };
    const preferredStoreMarkerConfig = React.useCallback((preferredStore, excludedFromMapBehavior = true)=>{
        return {
            markerId: preferredStore.globalStoreId,
            title: getLabel('appointments.component.storeFinderMap.map.pin.title'),
            position: {
                lat: Number(preferredStore.lat),
                lng: Number(preferredStore.lon)
            },
            pin: preferredStorePin || preferredStorePinFallback,
            infoWindow: activeMapInfoWindow === preferredStore.globalStoreId && !isPhone && /*#__PURE__*/ React.createElement(InfoWindow, {
                position: {
                    lat: Number(preferredStore.lat),
                    lng: Number(preferredStore.lon)
                },
                onCloseClick: handleOnMapClick
            }, /*#__PURE__*/ React.createElement(StoreFinderItemContainer, {
                variant: "map-item",
                store: fromStoreResultToStore(preferredStore),
                "data-t": "searchResultTile",
                onClick: handleOnClickPreferredStoreItem
            })),
            excludedFromMapBehavior
        };
    }, [
        activeMapInfoWindow,
        getLabel,
        handleOnMapClick,
        isPhone,
        preferredStorePin
    ]);
    const storeMarkers = React.useMemo(()=>{
        return stores.map((store)=>{
            const { globalStoreId , lat , lon  } = store;
            const storeIsPreferredStore = store.globalStoreId === (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.globalStoreId);
            if (storeIsPreferredStore) {
                return preferredStoreMarkerConfig(store, false);
            }
            return {
                markerId: globalStoreId.toString(),
                title: getLabel('appointments.component.storeFinderMap.map.pin.title'),
                position: {
                    lat: Number(lat),
                    lng: Number(lon)
                },
                infoWindow: activeMapInfoWindow === globalStoreId.toString() && !isPhone && /*#__PURE__*/ React.createElement(InfoWindow, {
                    position: {
                        lat: Number(lat),
                        lng: Number(lon)
                    },
                    onCloseClick: handleOnMapClick
                }, /*#__PURE__*/ React.createElement(StoreFinderItemContainer, {
                    variant: "map-item",
                    store: fromStoreResultToStore(store),
                    "data-t": "searchResultTile"
                }))
            };
        });
    }, [
        activeMapInfoWindow,
        getLabel,
        handleOnMapClick,
        isPhone,
        preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.globalStoreId,
        preferredStoreMarkerConfig,
        stores, 
    ]);
    const userLocationMarker = React.useMemo(()=>{
        if (isUsingUserLocation && userLocation) {
            return [
                {
                    position: {
                        lat: userLocation.lat,
                        lng: userLocation.lng
                    },
                    pin: currentLocationPin
                }, 
            ];
        }
        return [];
    }, [
        isUsingUserLocation,
        userLocation
    ]);
    const preferredStoreMarker = React.useMemo(()=>{
        const preferredStoreFoundInList = !!stores.filter((store)=>{
            return store.globalStoreId === (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.globalStoreId);
        }).length;
        // Only add preferred store marker here if not present in search results
        if (preferredStore1 && !preferredStoreFoundInList) {
            return [
                preferredStoreMarkerConfig(preferredStore1)
            ];
        }
        return [];
    }, [
        preferredStore1,
        preferredStoreMarkerConfig,
        stores
    ]);
    const markers = React.useMemo(()=>{
        return [
            ...storeMarkers,
            ...userLocationMarker,
            ...preferredStoreMarker
        ];
    }, [
        preferredStoreMarker,
        storeMarkers,
        userLocationMarker
    ]);
    const center = React.useMemo(()=>{
        if (isUsingUserLocation && userLocation !== undefined) {
            return userLocation;
        }
        return undefined;
    }, [
        userLocation,
        isUsingUserLocation
    ]);
    var ref;
    return /*#__PURE__*/ React.createElement(LocationSearchMap, {
        id: googleMapsId,
        markers: markers,
        markersUpdateConfig: {
            centerToFirst: true,
            zoom: 13
        },
        center: center,
        highlightMarkerId: (ref = activeStore === null || activeStore === void 0 ? void 0 : activeStore.globalStoreId) !== null && ref !== void 0 ? ref : null,
        hovered: hoveredStore !== null && hoveredStore !== void 0 ? hoveredStore : hoveredStoreInList === null || hoveredStoreInList === void 0 ? void 0 : hoveredStoreInList.globalStoreId,
        userLocation: isUsingUserLocation && userLocation ? userLocation : undefined,
        userLocationZoomLevel: 13,
        centerOnUserLocation: true,
        activeLocation: activeStore && isPhone ? /*#__PURE__*/ React.createElement(StoreFinderItemContainer, {
            variant: "map-item",
            store: activeStore
        }) // TODO: MACC-19077 Fix type and remove any before merging to master.
         : null,
        onClickUseMyLocation: handleOnClickUseMyLocation,
        onMarkerHover: handleOnMarkerHover,
        onMarkerClick: handleOnMarkerClick,
        onMapClick: handleOnMapClick
    });
};

export { StoreFinderMapContainer as default };
//# sourceMappingURL=StoreFinderMapContainer.js.map
